import * as React from 'react'
import { FormEvent, MouseEvent, PropsWithChildren, SetStateAction, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { EntityFormPage } from './Form'
import { AxiosPromiseAction, Dispatch } from '../../reducers'
import { AxiosResponse } from 'axios'
import { CommandButton, DefaultButton, mergeStyles, PrimaryButton, Stack, StackItem } from '@fluentui/react'
import DeleteDialog, { DeleteDialogProps } from '../../components/Dialog/LegacyDeleteDialog'
import DialogButton from '../../components/Button/DialogButton'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useTranslation } from 'react-i18next'

export type SubmitAction<V> = {
    (values: V): AxiosPromiseAction
}

// @todo remove history from props when FormAbstract is fully deprecated.
export type SubmitCallback = {
    (response?: AxiosResponse): void
}

export type FormButtonStateSetters<V> = {
    setAction: React.Dispatch<SetStateAction<SubmitAction<V>>>
    setCallback: React.Dispatch<SetStateAction<SubmitCallback>>
}

interface IButtonCallbackArguments<V> extends Array<any> {
    0: SubmitAction<V>
    1?: SubmitCallback
    2?: boolean // skipValidation flag.
}

type Props<V> = FormButtonStateSetters<V> &
    EntityFormPage &
    PropsWithChildren<{
        save?: IButtonCallbackArguments<V>
        saveAndNew?: IButtonCallbackArguments<V>
        delete?: IButtonCallbackArguments<V>
        cancel?: SubmitCallback
        lockedSave?: boolean
        lockedDelete?: boolean
    }>

export function getOnSubmitWithCallback<E>(dispatch: Dispatch, action: SubmitAction<E>, callback?: SubmitCallback) {
    return (values: E) => dispatch(action(values)).then(response => callback?.(response.value.data))
}

export default function FormButtons<V>(props: Props<V>) {
    const dispatch = useDispatch()
    const history = useHistory()
    const { t: translate } = useTranslation()

    const { submitForm, setSubmitting, values } = useFormikContext<V>()
    const { isViewport, viewport, getGap } = useViewport()
    const buttonStyles = useMemo(() => mergeStyles({ width: isViewport('<', Viewport.OutlookNew) ? '100%' : undefined }), [viewport])

    const returnCallback: SubmitCallback = () => (props.returnUrl ? history.push(props.returnUrl) : history.goBack())
    const submit = (event: FormEvent, action: SubmitAction<V>, callback?: SubmitCallback, skipValidation?: boolean) => {
        // Prevent the default Formik event handler from adding form values to the URL which results in a reset
        // in Outlook.
        event.preventDefault()
        callback = callback || returnCallback
        setSubmitting(true)

        // Directly submit when we skip validation.
        if (skipValidation) {
            getOnSubmitWithCallback(dispatch, action, callback)(values)
        } else {
            props.setAction(() => action)
            props.setCallback(() => callback)
            submitForm()
        }
    }

    return (
        <Stack
            horizontal={isViewport('>=', Viewport.OutlookWeb)}
            verticalAlign="center"
            wrap
            tokens={{ childrenGap: getGap(), padding: `${getGap()} 0` }}
        >
            {props.save && (
                <StackItem>
                    <PrimaryButton
                        text={translate('action.save')}
                        type="submit"
                        className={buttonStyles}
                        {...(props.saveAndNew && { tabIndex: -1 })}
                        disabled={props.lockedSave || false}
                        onClick={(event: MouseEvent<HTMLElement>) => submit(event, props.save.shift(), ...props.save)}
                    />
                </StackItem>
            )}
            {props.saveAndNew && (
                <StackItem grow={isViewport('<', Viewport.OutlookNew)}>
                    <PrimaryButton
                        text={translate('action.saveandnew')}
                        className={buttonStyles}
                        disabled={props.lockedSave || false}
                        onClick={(event: MouseEvent<HTMLElement>) => submit(event, props.saveAndNew.shift(), ...props.saveAndNew)}
                    />
                </StackItem>
            )}
            <StackItem grow={isViewport('<', Viewport.OutlookNew)}>
                <DefaultButton
                    text={translate('action.cancel')}
                    className={buttonStyles}
                    onClick={() => (props.cancel ? props.cancel() : returnCallback())}
                />
            </StackItem>
            <StackItem grow>
                <span />
            </StackItem>
            {props.delete && (
                <StackItem grow={isViewport('<', Viewport.OutlookNew)}>
                    <DialogButton<DeleteDialogProps>
                        button={CommandButton}
                        buttonProps={{
                            text: translate('action.delete'),
                            className: buttonStyles,
                            styles: { flexContainer: { justifyContent: 'center' } },
                            iconProps: { iconName: 'Delete' },
                            disabled: props.lockedDelete || false,
                        }}
                        dialog={DeleteDialog}
                        dialogProps={{ onDelete: event => submit(event, props.delete.shift(), ...props.delete) }}
                    />
                </StackItem>
            )}
        </Stack>
    )
}
